<template>
  <AdminLayout page-title="Payroll Management" page-subtitle="Manage contractor payments and payroll processing">
    <div class="space-y-8">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Total Payouts</p>
              <p class="text-3xl font-bold text-white">${{ stats.totalPayouts.toLocaleString() }}</p>
              <p class="text-sm text-emerald-400 mt-2">This month</p>
            </div>
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Pending Payments</p>
              <p class="text-3xl font-bold text-white">{{ stats.pendingPayments }}</p>
              <p class="text-sm text-yellow-400 mt-2">Awaiting approval</p>
            </div>
            <div class="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Active Contractors</p>
              <p class="text-3xl font-bold text-white">{{ stats.activeContractors }}</p>
              <p class="text-sm text-emerald-400 mt-2">Currently working</p>
            </div>
            <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Avg. Daily Rate</p>
              <p class="text-3xl font-bold text-white">${{ stats.avgDailyRate }}</p>
              <p class="text-sm text-slate-400 mt-2">Per contractor</p>
            </div>
            <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <button
          @click="showCreatePayrollModal = true"
          class="admin-action-card group"
        >
          <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-500/30 transition-colors">
            <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-white mb-2">New Payroll Run</h3>
          <p class="text-slate-400 text-sm">Process payments for contractors</p>
        </button>

        <button
          @click="showBulkApprovalModal = true"
          class="admin-action-card group"
        >
          <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-500/30 transition-colors">
            <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-white mb-2">Bulk Approval</h3>
          <p class="text-slate-400 text-sm">Approve multiple payments at once</p>
        </button>

        <button
          @click="showAddContractorModal = true"
          class="admin-action-card group"
        >
          <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500/30 transition-colors">
            <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-white mb-2">Add Contractor</h3>
          <p class="text-slate-400 text-sm">Register new contractor</p>
        </button>

        <button
          @click="generateReports"
          class="admin-action-card group"
        >
          <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/30 transition-colors">
            <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-white mb-2">Generate Reports</h3>
          <p class="text-slate-400 text-sm">Export payroll and tax reports</p>
        </button>
      </div>

      <!-- Filters -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div class="flex items-center space-x-4">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search contractors..."
              class="w-64 pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
            <svg class="absolute left-3 top-2.5 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <select
            v-model="filterStatus"
            class="px-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
          </select>
          <select
            v-model="filterPeriod"
            class="px-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">All Time</option>
            <option value="this-week">This Week</option>
            <option value="this-month">This Month</option>
            <option value="last-month">Last Month</option>
          </select>
        </div>
        <button
          @click="exportPayroll"
          class="px-4 py-2 bg-slate-700/50 border border-slate-600/50 text-slate-300 hover:text-white hover:border-emerald-500/30 rounded-lg transition-all duration-300"
        >
          <svg class="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          Export
        </button>
      </div>

      <!-- Payroll Table -->
      <div class="admin-stat-card overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-slate-700/50">
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Contractor</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Job Site</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Period</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Hours</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Rate</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Expenses</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Total Amount</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Status</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in filteredPayments" :key="payment.id" class="border-b border-slate-700/30 hover:bg-slate-800/20 transition-colors">
                <td class="py-4 px-6">
                  <div class="flex items-center space-x-3">
                    <div class="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center text-slate-900 font-semibold">
                      {{ payment.contractor.charAt(0) }}
                    </div>
                    <div>
                      <p class="font-semibold text-white">{{ payment.contractor }}</p>
                      <p class="text-sm text-slate-400">{{ payment.email }}</p>
                    </div>
                  </div>
                </td>
                <td class="py-4 px-6">
                  <div>
                    <p class="text-white font-medium">{{ payment.jobSite }}</p>
                    <p class="text-sm text-slate-400">{{ payment.location }}</p>
                  </div>
                </td>
                <td class="py-4 px-6 text-slate-300">{{ payment.period }}</td>
                <td class="py-4 px-6 text-white font-semibold">{{ payment.hoursWorked }}h</td>
                <td class="py-4 px-6 text-white">${{ payment.hourlyRate }}/hr</td>
                <td class="py-4 px-6 text-white">${{ payment.expenses.toLocaleString() }}</td>
                <td class="py-4 px-6 text-white font-bold">${{ payment.totalAmount.toLocaleString() }}</td>
                <td class="py-4 px-6">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="getStatusBadgeClass(payment.status)">
                    {{ payment.status.charAt(0).toUpperCase() + payment.status.slice(1) }}
                  </span>
                </td>
                <td class="py-4 px-6">
                  <div class="flex items-center space-x-2">
                    <button
                      v-if="payment.status === 'pending'"
                      @click="approvePayment(payment.id)"
                      class="p-2 text-slate-400 hover:text-emerald-400 transition-colors"
                      title="Approve Payment"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    </button>
                    <button
                      @click="viewPaymentDetails(payment)"
                      class="p-2 text-slate-400 hover:text-cyan-400 transition-colors"
                      title="View Details"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                    <button
                      @click="editPayment(payment)"
                      class="p-2 text-slate-400 hover:text-blue-400 transition-colors"
                      title="Edit Payment"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button
                      v-if="payment.status !== 'paid'"
                      @click="rejectPayment(payment.id)"
                      class="p-2 text-slate-400 hover:text-red-400 transition-colors"
                      title="Reject Payment"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="filteredPayments.length === 0" class="text-center py-16">
          <svg class="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
          <h3 class="text-lg font-medium text-white mb-2">No Payments Found</h3>
          <p class="text-slate-400 mb-6">No payments match your current filters</p>
          <button @click="clearFilters" class="btn-hero-primary">
            Clear Filters
          </button>
        </div>
      </div>

      <!-- Recent Payroll Runs -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-semibold text-white">Recent Payroll Runs</h3>
            <p class="text-sm text-slate-400">Track payroll processing history</p>
          </div>
          <button @click="showPayrollHistoryModal = true" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
            View All
          </button>
        </div>
        <div class="space-y-4">
          <div v-for="run in recentPayrollRuns" :key="run.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
            <div class="flex items-center space-x-4">
              <div class="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                </svg>
              </div>
              <div>
                <p class="font-semibold text-white">{{ run.description }}</p>
                <p class="text-sm text-slate-400">{{ run.period }} • {{ run.contractorsCount }} contractors</p>
              </div>
            </div>
            <div class="text-right">
              <p class="font-bold text-white">${{ run.totalAmount.toLocaleString() }}</p>
              <p class="text-sm text-slate-400">{{ formatDate(run.processedAt) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Payroll Modal -->
    <div v-if="showCreatePayrollModal" class="modal-overlay" @click="closeCreatePayrollModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Create Payroll Run</h3>
          <button @click="closeCreatePayrollModal" class="modal-close">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        <form @submit.prevent="createPayrollRun" class="modal-body">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Pay Period Start</label>
              <input
                v-model="newPayroll.startDate"
                type="date"
                required
                class="form-input"
              >
            </div>
            <div class="form-group">
              <label class="form-label">Pay Period End</label>
              <input
                v-model="newPayroll.endDate"
                type="date"
                required
                class="form-input"
              >
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Description</label>
            <input
              v-model="newPayroll.description"
              type="text"
              required
              class="form-input"
              placeholder="e.g., Weekly payroll for January 8-14, 2024"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Job Sites</label>
            <div class="space-y-2">
              <label v-for="site in jobSites" :key="site.id" class="flex items-center space-x-3">
                <input
                  type="checkbox"
                  :value="site.id"
                  v-model="newPayroll.selectedSites"
                  class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500"
                >
                <span class="text-sm text-slate-300">{{ site.name }} ({{ site.location }})</span>
              </label>
            </div>
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeCreatePayrollModal" class="btn-secondary">
              Cancel
            </button>
            <button type="submit" class="btn-hero-primary">
              Create Payroll Run
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Bulk Approval Modal -->
    <div v-if="showBulkApprovalModal" class="modal-overlay" @click="closeBulkApprovalModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Bulk Approve Payments</h3>
          <button @click="closeBulkApprovalModal" class="modal-close">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <p class="text-slate-300 mb-4">
            You are about to approve {{ pendingPayments.length }} pending payments totaling 
            <span class="font-bold text-emerald-400">${{ totalPendingAmount.toLocaleString() }}</span>
          </p>
          <div class="bg-slate-700/30 rounded-lg p-4 mb-6">
            <h4 class="text-sm font-semibold text-slate-300 mb-3">Pending Payments:</h4>
            <div class="space-y-2 max-h-40 overflow-y-auto">
              <div v-for="payment in pendingPayments" :key="payment.id" class="flex justify-between items-center text-sm">
                <div>
                  <span class="text-slate-300">{{ payment.contractor }}</span>
                  <span class="text-slate-500 ml-2">{{ payment.jobSite }}</span>
                </div>
                <span class="text-white font-medium">${{ payment.totalAmount.toLocaleString() }}</span>
              </div>
            </div>
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeBulkApprovalModal" class="btn-secondary">
              Cancel
            </button>
            <button @click="bulkApprovePayments" class="btn-hero-primary">
              Approve All ({{ pendingPayments.length }})
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Contractor Modal -->
    <div v-if="showAddContractorModal" class="modal-overlay" @click="closeAddContractorModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Add New Contractor</h3>
          <button @click="closeAddContractorModal" class="modal-close">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        <form @submit.prevent="addContractor" class="modal-body">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Full Name</label>
              <input
                v-model="newContractor.name"
                type="text"
                required
                class="form-input"
                placeholder="John Smith"
              >
            </div>
            <div class="form-group">
              <label class="form-label">Email</label>
              <input
                v-model="newContractor.email"
                type="email"
                required
                class="form-input"
                placeholder="john@contractor.com"
              >
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Phone</label>
              <input
                v-model="newContractor.phone"
                type="tel"
                required
                class="form-input"
                placeholder="(555) 123-4567"
              >
            </div>
            <div class="form-group">
              <label class="form-label">Hourly Rate</label>
              <input
                v-model.number="newContractor.hourlyRate"
                type="number"
                min="1"
                step="0.01"
                required
                class="form-input"
                placeholder="25.00"
              >
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Specialization</label>
            <select v-model="newContractor.specialization" required class="form-select">
              <option value="">Select Specialization</option>
              <option value="drilling">Drilling Operations</option>
              <option value="completion">Well Completion</option>
              <option value="maintenance">Equipment Maintenance</option>
              <option value="safety">Safety Oversight</option>
              <option value="transport">Transportation</option>
              <option value="general">General Labor</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Tax Information</label>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                v-model="newContractor.ssn"
                type="text"
                placeholder="SSN (XXX-XX-XXXX)"
                class="form-input"
              >
              <input
                v-model="newContractor.taxId"
                type="text"
                placeholder="Tax ID (Optional)"
                class="form-input"
              >
            </div>
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeAddContractorModal" class="btn-secondary">
              Cancel
            </button>
            <button type="submit" class="btn-hero-primary">
              Add Contractor
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Payment Details Modal -->
    <div v-if="showPaymentDetailsModal" class="modal-overlay" @click="closePaymentDetailsModal">
      <div class="modal-content modal-large" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Payment Details</h3>
          <button @click="closePaymentDetailsModal" class="modal-close">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        <div v-if="selectedPayment" class="modal-body">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <!-- Contractor Information -->
            <div class="space-y-6">
              <div class="bg-slate-700/30 rounded-xl p-6">
                <h4 class="text-lg font-semibold text-white mb-4">Contractor Information</h4>
                <div class="space-y-3">
                  <div class="flex justify-between">
                    <span class="text-slate-400">Name:</span>
                    <span class="text-white font-medium">{{ selectedPayment.contractor }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-400">Email:</span>
                    <span class="text-white">{{ selectedPayment.email }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-400">Job Site:</span>
                    <span class="text-white">{{ selectedPayment.jobSite }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-400">Location:</span>
                    <span class="text-white">{{ selectedPayment.location }}</span>
                  </div>
                </div>
              </div>

              <!-- Work Details -->
              <div class="bg-slate-700/30 rounded-xl p-6">
                <h4 class="text-lg font-semibold text-white mb-4">Work Details</h4>
                <div class="space-y-3">
                  <div class="flex justify-between">
                    <span class="text-slate-400">Period:</span>
                    <span class="text-white">{{ selectedPayment.period }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-400">Hours Worked:</span>
                    <span class="text-white font-medium">{{ selectedPayment.hoursWorked }}h</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-400">Hourly Rate:</span>
                    <span class="text-white">${{ selectedPayment.hourlyRate }}/hr</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-400">Base Pay:</span>
                    <span class="text-white">${{ (selectedPayment.hoursWorked * selectedPayment.hourlyRate).toLocaleString() }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Summary -->
            <div class="space-y-6">
              <div class="bg-slate-700/30 rounded-xl p-6">
                <h4 class="text-lg font-semibold text-white mb-4">Payment Summary</h4>
                <div class="space-y-3">
                  <div class="flex justify-between">
                    <span class="text-slate-400">Base Pay:</span>
                    <span class="text-white">${{ (selectedPayment.hoursWorked * selectedPayment.hourlyRate).toLocaleString() }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-400">Expenses:</span>
                    <span class="text-white">${{ selectedPayment.expenses.toLocaleString() }}</span>
                  </div>
                  <div class="border-t border-slate-600 pt-3 flex justify-between">
                    <span class="text-white font-semibold">Total Amount:</span>
                    <span class="text-emerald-400 font-bold text-lg">${{ selectedPayment.totalAmount.toLocaleString() }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-400">Status:</span>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="getStatusBadgeClass(selectedPayment.status)">
                      {{ selectedPayment.status.charAt(0).toUpperCase() + selectedPayment.status.slice(1) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="space-y-3">
                <button
                  v-if="selectedPayment.status === 'pending'"
                  @click="approvePayment(selectedPayment.id)"
                  class="w-full btn-hero-primary"
                >
                  Approve Payment
                </button>
                <button
                  @click="editPayment(selectedPayment)"
                  class="w-full btn-secondary"
                >
                  Edit Payment
                </button>
                <button
                  v-if="selectedPayment.status !== 'paid'"
                  @click="rejectPayment(selectedPayment.id)"
                  class="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  Reject Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAdminStore } from '../../stores/adminStore'
import AdminLayout from './AdminLayout.vue'

const adminStore = useAdminStore()

interface Payment {
  id: number
  contractor: string
  email: string
  jobSite: string
  location: string
  period: string
  hoursWorked: number
  hourlyRate: number
  expenses: number
  totalAmount: number
  status: 'pending' | 'approved' | 'paid' | 'failed'
  createdAt: string
}

interface PayrollRun {
  id: number
  description: string
  period: string
  contractorsCount: number
  totalAmount: number
  processedAt: string
  status: string
}

interface JobSite {
  id: number
  name: string
  location: string
}

interface NewContractor {
  name: string
  email: string
  phone: string
  hourlyRate: number
  specialization: string
  ssn: string
  taxId: string
}

// Reactive state
const searchQuery = ref('')
const filterStatus = ref('')
const filterPeriod = ref('')
const showCreatePayrollModal = ref(false)
const showBulkApprovalModal = ref(false)
const showAddContractorModal = ref(false)
const showPaymentDetailsModal = ref(false)
const selectedPayment = ref<Payment | null>(null)

const newPayroll = ref({
  startDate: '',
  endDate: '',
  description: '',
  selectedSites: [] as number[]
})

const newContractor = ref<NewContractor>({
  name: '',
  email: '',
  phone: '',
  hourlyRate: 0,
  specialization: '',
  ssn: '',
  taxId: ''
})

// State
const payments = ref<Payment[]>([])
const isLoading = ref(false)

const recentPayrollRuns = ref<PayrollRun[]>([])
const jobSites = ref<JobSite[]>([])

// Computed properties
const stats = computed(() => {
  const list = Array.isArray(payments.value) ? payments.value : []
  const totalPayouts = list
    .filter(p => p.status === 'paid')
    .reduce((sum, p) => sum + Number(p.totalAmount || 0), 0)
  
  const pendingCount = list.filter(p => p.status === 'pending').length
  const activeContractors = new Set(list.map(p => p.contractor)).size
  const avgRate = list.length
    ? Math.round(list.reduce((sum, p) => sum + Number(p.hourlyRate || 0), 0) / list.length)
    : 0

  return {
    totalPayouts,
    pendingPayments: pendingCount,
    activeContractors,
    avgDailyRate: avgRate
  }
})

const filteredPayments = computed(() => {
  let filtered = payments.value

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(payment => 
      payment.contractor.toLowerCase().includes(query) ||
      payment.email.toLowerCase().includes(query) ||
      payment.jobSite.toLowerCase().includes(query)
    )
  }

  if (filterStatus.value) {
    filtered = filtered.filter(payment => payment.status === filterStatus.value)
  }

  if (filterPeriod.value) {
    const now = new Date()
    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()))
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0)

    filtered = filtered.filter(payment => {
      const paymentDate = new Date(payment.createdAt)
      switch (filterPeriod.value) {
        case 'this-week':
          return paymentDate >= startOfWeek
        case 'this-month':
          return paymentDate >= startOfMonth
        case 'last-month':
          return paymentDate >= startOfLastMonth && paymentDate <= endOfLastMonth
        default:
          return true
      }
    })
  }

  return filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
})

const pendingPayments = computed(() => {
  return payments.value.filter(payment => payment.status === 'pending')
})

const totalPendingAmount = computed(() => {
  return pendingPayments.value.reduce((total, payment) => total + payment.totalAmount, 0)
})

// Methods
const getStatusBadgeClass = (status: string) => {
  const classes = {
    'pending': 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
    'approved': 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    'paid': 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    'failed': 'bg-red-500/20 text-red-400 border border-red-500/30'
  }
  return classes[status] || classes.pending
}

const approvePayment = (paymentId: number) => {
  const payment = payments.value.find(p => p.id === paymentId)
  if (payment) {
    payment.status = 'approved'
    if (showPaymentDetailsModal.value) {
      closePaymentDetailsModal()
    }
  }
}

const rejectPayment = (paymentId: number) => {
  if (confirm('Are you sure you want to reject this payment? This action cannot be undone.')) {
    const payment = payments.value.find(p => p.id === paymentId)
    if (payment) {
      payment.status = 'failed'
      if (showPaymentDetailsModal.value) {
        closePaymentDetailsModal()
      }
    }
  }
}

const viewPaymentDetails = (payment: Payment) => {
  selectedPayment.value = payment
  showPaymentDetailsModal.value = true
}

const editPayment = (payment: Payment) => {
  // TODO: Implement edit payment functionality
  console.log('Edit payment:', payment)
  alert('Edit payment functionality coming soon!')
}

const createPayrollRun = () => {
  if (newPayroll.value.selectedSites.length === 0) {
    alert('Please select at least one job site.')
    return
  }

  const newRun: PayrollRun = {
    id: Date.now(),
    description: newPayroll.value.description,
    period: `${newPayroll.value.startDate} to ${newPayroll.value.endDate}`,
    contractorsCount: Math.floor(Math.random() * 20) + 10,
    totalAmount: Math.floor(Math.random() * 100000) + 50000,
    processedAt: new Date().toISOString(),
    status: 'processing'
  }

  recentPayrollRuns.value.unshift(newRun)
  closeCreatePayrollModal()
  
  // Simulate processing
  setTimeout(() => {
    const run = recentPayrollRuns.value.find(r => r.id === newRun.id)
    if (run) run.status = 'completed'
  }, 3000)
}

const bulkApprovePayments = () => {
  pendingPayments.value.forEach(payment => {
    payment.status = 'approved'
  })
  closeBulkApprovalModal()
}

const addContractor = () => {
  // TODO: Implement add contractor functionality
  console.log('Add contractor:', newContractor.value)
  
  // Reset form
  newContractor.value = {
    name: '',
    email: '',
    phone: '',
    hourlyRate: 0,
    specialization: '',
    ssn: '',
    taxId: ''
  }
  
  closeAddContractorModal()
  alert('Contractor added successfully!')
}

const generateReports = () => {
  // TODO: Implement report generation
  alert('Generating payroll reports...\n\n• Tax Summary Report\n• Contractor Payment Report\n• Job Site Cost Analysis\n• Compliance Report')
}

const exportPayroll = () => {
  // TODO: Implement export functionality
  const csvContent = 'data:text/csv;charset=utf-8,' + 
    'Contractor,Job Site,Period,Hours,Rate,Expenses,Total,Status\n' +
    filteredPayments.value.map(p => 
      `${p.contractor},${p.jobSite},${p.period},${p.hoursWorked},${p.hourlyRate},${p.expenses},${p.totalAmount},${p.status}`
    ).join('\n')
  
  const encodedUri = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  link.setAttribute('download', `payroll-export-${new Date().toISOString().split('T')[0]}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const clearFilters = () => {
  searchQuery.value = ''
  filterStatus.value = ''
  filterPeriod.value = ''
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

// Modal management
const closeCreatePayrollModal = () => {
  showCreatePayrollModal.value = false
  newPayroll.value = { startDate: '', endDate: '', description: '', selectedSites: [] }
}

const closeBulkApprovalModal = () => {
  showBulkApprovalModal.value = false
}

const closeAddContractorModal = () => {
  showAddContractorModal.value = false
  newContractor.value = {
    name: '',
    email: '',
    phone: '',
    hourlyRate: 0,
    specialization: '',
    ssn: '',
    taxId: ''
  }
}

const closePaymentDetailsModal = () => {
  showPaymentDetailsModal.value = false
  selectedPayment.value = null
}

const loadPayrollData = async () => {
  try {
    isLoading.value = true
    const response = await fetch('/api/admin/payroll', {
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      const rows = Array.isArray(data.payments) ? data.payments : (Array.isArray(data.payroll) ? data.payroll : [])
      payments.value = rows.map((row: any) => ({
        ...row,
        contractor: row.contractor || row.worker || row.name || 'Contractor',
        email: row.email || '',
        jobSite: row.jobSite || row.site || '',
        totalAmount: Number(row.totalAmount ?? row.net ?? row.gross ?? 0),
        hourlyRate: Number(row.hourlyRate || 0),
        expenses: Number(row.expenses || 0),
        hoursWorked: Number(row.hoursWorked || 0),
        status: row.status || 'pending',
        createdAt: row.createdAt || row.payDate || new Date().toISOString(),
      }))
      recentPayrollRuns.value = Array.isArray(data.payrollRuns) ? data.payrollRuns : []
      jobSites.value = Array.isArray(data.jobSites) ? data.jobSites : []
    } else {
      // Load mock data for demo
      loadMockData()
    }
  } catch (error) {
    console.error('Error loading payroll data:', error)
    loadMockData()
  } finally {
    isLoading.value = false
  }
}

const loadMockData = () => {
  payments.value = [
    {
      id: 1,
      contractor: 'John Smith',
      email: 'john@contractor.com',
      jobSite: 'Bakken Drilling Site A',
      location: 'Williston, ND',
      period: 'Jan 8-14, 2024',
      hoursWorked: 84,
      hourlyRate: 45,
      expenses: 280,
      totalAmount: 4060,
      status: 'pending',
      createdAt: '2024-01-15T10:30:00Z'
    },
    {
      id: 2,
      contractor: 'Sarah Johnson',
      email: 'sarah@drilling.com',
      jobSite: 'Eagle Ford Completion',
      location: 'Karnes City, TX',
      period: 'Jan 8-14, 2024',
      hoursWorked: 72,
      hourlyRate: 52,
      expenses: 190,
      totalAmount: 3934,
      status: 'approved',
      createdAt: '2024-01-14T14:20:00Z'
    },
    {
      id: 3,
      contractor: 'Mike Wilson',
      email: 'mike@oilfield.com',
      jobSite: 'Permian Basin Rig 7',
      location: 'Midland, TX',
      period: 'Jan 8-14, 2024',
      hoursWorked: 96,
      hourlyRate: 48,
      expenses: 320,
      totalAmount: 4928,
      status: 'paid',
      createdAt: '2024-01-13T09:15:00Z'
    },
    {
      id: 4,
      contractor: 'Emily Davis',
      email: 'emily@energy.com',
      jobSite: 'Marcellus Shale Site B',
      location: 'Washington, PA',
      period: 'Jan 1-7, 2024',
      hoursWorked: 60,
      hourlyRate: 41,
      expenses: 150,
      totalAmount: 2610,
      status: 'pending',
      createdAt: '2024-01-08T16:45:00Z'
    },
    {
      id: 5,
      contractor: 'Robert Brown',
      email: 'robert@contractor.com',
      jobSite: 'Haynesville Formation',
      location: 'Shreveport, LA',
      period: 'Jan 1-7, 2024',
      hoursWorked: 48,
      hourlyRate: 39,
      expenses: 120,
      totalAmount: 1992,
      status: 'failed',
      createdAt: '2024-01-07T11:30:00Z'
    },
    {
      id: 6,
      contractor: 'Lisa Martinez',
      email: 'lisa@oiltech.com',
      jobSite: 'Niobrara Formation',
      location: 'Weld County, CO',
      period: 'Jan 8-14, 2024',
      hoursWorked: 78,
      hourlyRate: 46,
      expenses: 210,
      totalAmount: 3798,
      status: 'approved',
      createdAt: '2024-01-14T08:20:00Z'
    }
  ]
  
  recentPayrollRuns.value = [
    {
      id: 1,
      description: 'Weekly Payroll - Week 2',
      period: 'Jan 8-14, 2024',
      contractorsCount: 23,
      totalAmount: 127450,
      processedAt: '2024-01-15T10:00:00Z',
      status: 'completed'
    },
    {
      id: 2,
      description: 'Weekly Payroll - Week 1',
      period: 'Jan 1-7, 2024',
      contractorsCount: 19,
      totalAmount: 98720,
      processedAt: '2024-01-08T10:00:00Z',
      status: 'completed'
    },
    {
      id: 3,
      description: 'Holiday Bonus Payments',
      period: 'Dec 2023',
      contractorsCount: 31,
      totalAmount: 45600,
      processedAt: '2023-12-28T15:30:00Z',
      status: 'completed'
    }
  ]
  
  jobSites.value = [
    { id: 1, name: 'Bakken Drilling Site A', location: 'Williston, ND' },
    { id: 2, name: 'Eagle Ford Completion', location: 'Karnes City, TX' },
    { id: 3, name: 'Permian Basin Rig 7', location: 'Midland, TX' },
    { id: 4, name: 'Marcellus Shale Site B', location: 'Washington, PA' },
    { id: 5, name: 'Haynesville Formation', location: 'Shreveport, LA' },
    { id: 6, name: 'Niobrara Formation', location: 'Weld County, CO' }
  ]
}

// Lifecycle
onMounted(() => {
  loadPayrollData()
  
  // Initialize with current week dates
  const now = new Date()
  const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()))
  const endOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 6))
  
  newPayroll.value.startDate = startOfWeek.toISOString().split('T')[0]
  newPayroll.value.endDate = endOfWeek.toISOString().split('T')[0]
})
</script>

<style scoped>
.admin-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

.admin-action-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300 text-left;
}

/* Modal Styles */
.modal-overlay {
  @apply fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50;
}

.modal-content {
  @apply bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden;
}

.modal-large {
  @apply max-w-4xl;
}

.modal-header {
  @apply flex items-center justify-between p-6 border-b border-slate-700/50;
}

.modal-title {
  @apply text-lg font-medium text-white;
}

.modal-close {
  @apply text-slate-400 hover:text-slate-300 transition-colors;
}

.modal-body {
  @apply p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)];
}

.modal-actions {
  @apply flex items-center justify-end space-x-3 pt-4 border-t border-slate-700/50;
}

/* Form Styles */
.form-group {
  @apply mb-6;
}

.form-label {
  @apply block text-sm font-medium text-slate-300 mb-3;
}

.form-input {
  @apply w-full bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200;
}

.form-select {
  @apply w-full bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200;
}

/* Button Styles */
.btn-hero-primary {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

.btn-secondary {
  @apply inline-flex items-center px-4 py-2 bg-slate-700/50 text-slate-300 font-medium rounded-lg hover:bg-slate-600/50 transition-colors duration-200;
}
</style>